import { testimonials, businessInfo } from '../data/business';
import { Star, Quote, Phone, Mail, MapPin, Navigation, Clock, Facebook, Instagram, ChevronLeft, ChevronRight } from 'lucide-react';
import { useState, useEffect } from 'react';

export default function ContactAndTestimonials() {
  const [currentTestimonial, setCurrentTestimonial] = useState(0);

  const nextTestimonial = () => {
    setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setCurrentTestimonial((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  // Auto-advance carousel
  useEffect(() => {
    const timer = setInterval(nextTestimonial, 5000);
    return () => clearInterval(timer);
  }, []);

  return (
    <>
      {/* Testimonials Section */}
      <section id="testimonials" className="section-padding bg-gray-50 border-t border-gray-200">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center max-w-4xl mx-auto mb-12 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-heading font-extrabold text-text mb-4 sm:mb-6">
              Utisci Naših <span className="text-primary">Klijenata</span>
            </h2>
            <p className="text-gray-800 text-lg sm:text-xl font-medium">
              Pouzdanost i profesionalnost koje klijenti prepoznaju.
            </p>
          </div>

          <div className="max-w-3xl mx-auto relative">
            {/* Carousel Container */}
            <div className="bg-white p-8 sm:p-12 rounded shadow-md border-b-4 border-primary relative overflow-hidden min-h-[300px] flex flex-col justify-center">
              <Quote className="absolute top-6 left-6 text-gray-100 w-16 h-16 sm:w-20 sm:h-20 fill-gray-100 -z-0" />
              
              <div className="relative z-10 text-center">
                <div className="flex justify-center gap-1 mb-6">
                  {[...Array(testimonials[currentTestimonial].rating)].map((_, i) => (
                    <Star key={i} size={24} className="text-primary fill-primary" />
                  ))}
                </div>
                
                <p className="text-gray-800 text-xl sm:text-2xl mb-8 italic font-medium leading-relaxed">
                  "{testimonials[currentTestimonial].text}"
                </p>
                
                <div className="border-t border-gray-100 pt-6 inline-block px-10">
                  <p className="font-bold text-text text-xl">{testimonials[currentTestimonial].author}</p>
                  <p className="text-primary font-medium">{testimonials[currentTestimonial].location}</p>
                </div>
              </div>
            </div>

            {/* Navigation Buttons */}
            <button 
              onClick={prevTestimonial}
              className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 sm:-translate-x-12 bg-white text-primary p-3 rounded-full shadow-lg hover:bg-primary hover:text-white transition-all border border-gray-100 z-20"
              aria-label="Prethodna recenzija"
            >
              <ChevronLeft size={24} />
            </button>
            
            <button 
              onClick={nextTestimonial}
              className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 sm:translate-x-12 bg-white text-primary p-3 rounded-full shadow-lg hover:bg-primary hover:text-white transition-all border border-gray-100 z-20"
              aria-label="Sledeća recenzija"
            >
              <ChevronRight size={24} />
            </button>

            {/* Dots Indicator */}
            <div className="flex justify-center gap-2 mt-8">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentTestimonial(index)}
                  className={`w-3 h-3 rounded-full transition-all ${
                    currentTestimonial === index ? 'bg-primary w-6' : 'bg-gray-300 hover:bg-gray-400'
                  }`}
                  aria-label={`Idi na recenziju ${index + 1}`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="relative section-padding bg-white overflow-hidden">
        <div className="container mx-auto px-4 md:px-6 relative z-10">
          <div className="text-center max-w-4xl mx-auto mb-12 sm:mb-16">
            <div className="inline-block px-3 py-1 bg-white text-primary font-bold text-sm uppercase tracking-wider mb-4 sm:mb-6 border-b-2 border-primary">
              Kontakt
            </div>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-heading font-extrabold text-text mb-4 sm:mb-6">
              Kontaktirajte Nas
            </h2>
            <p className="text-gray-800 text-lg sm:text-xl font-medium">
              Za sve informacije, procene radova i hitne intervencije – tu smo za vas.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-start">
            {/* Contact Info */}
            <div className="space-y-8 sm:space-y-10">
              <div className="bg-white p-6 sm:p-10 rounded shadow-xl border border-gray-100 relative overflow-hidden">
                <div className="absolute top-0 left-0 w-2 h-full bg-primary"></div>
                
                <div className="flex flex-col gap-8 sm:gap-10">
                  <a href={`tel:${businessInfo.phoneClean}`} className="flex items-center gap-6 sm:gap-8 group">
                    <div className="w-14 h-14 sm:w-16 sm:h-16 bg-primary text-white rounded flex items-center justify-center shadow-lg shadow-primary/30 group-hover:scale-110 transition-transform shrink-0">
                      <Phone size={28} strokeWidth={2} className="sm:w-8 sm:h-8" />
                    </div>
                    <div>
                      <p className="text-gray-500 text-xs sm:text-sm uppercase tracking-wider font-bold mb-1 sm:mb-2">Pozovite nas</p>
                      <p className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-text group-hover:text-primary transition-colors">{businessInfo.phone}</p>
                    </div>
                  </a>

                  <a href={`mailto:${businessInfo.email}`} className="flex items-center gap-6 sm:gap-8 group">
                    <div className="w-14 h-14 sm:w-16 sm:h-16 bg-white text-primary border-2 border-primary rounded flex items-center justify-center group-hover:bg-primary group-hover:text-white transition-all shrink-0">
                      <Mail size={28} strokeWidth={2} className="sm:w-8 sm:h-8" />
                    </div>
                    <div className="overflow-hidden">
                      <p className="text-gray-500 text-xs sm:text-sm uppercase tracking-wider font-bold mb-1 sm:mb-2">Pošaljite email</p>
                      <p className="text-lg sm:text-xl md:text-2xl font-bold text-text break-all group-hover:text-primary transition-colors">{businessInfo.email}</p>
                    </div>
                  </a>

                  <div className="flex items-center gap-6 sm:gap-8">
                    <div className="w-14 h-14 sm:w-16 sm:h-16 bg-gray-50 text-text border border-gray-200 rounded flex items-center justify-center shrink-0">
                      <MapPin size={28} strokeWidth={2} className="sm:w-8 sm:h-8" />
                    </div>
                    <div>
                      <p className="text-gray-500 text-xs sm:text-sm uppercase tracking-wider font-bold mb-1 sm:mb-2">Lokacija</p>
                      <p className="text-lg sm:text-xl font-bold text-text">{businessInfo.address}</p>
                      <p className="text-primary font-medium">{businessInfo.location}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-6 sm:gap-8">
                    <div className="w-14 h-14 sm:w-16 sm:h-16 bg-gray-50 text-text border border-gray-200 rounded flex items-center justify-center shrink-0">
                      <Clock size={28} strokeWidth={2} className="sm:w-8 sm:h-8" />
                    </div>
                    <div>
                      <p className="text-gray-500 text-xs sm:text-sm uppercase tracking-wider font-bold mb-1 sm:mb-2">Radno Vreme</p>
                      <p className="text-lg sm:text-xl font-bold text-text">Svaki dan — po dogovoru</p>
                    </div>
                  </div>
                </div>

                <div className="mt-10 sm:mt-12 pt-8 sm:pt-10 border-t border-gray-100 flex flex-col sm:flex-row gap-4 sm:gap-6">
                  <a 
                    href={businessInfo.mapsLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 bg-primary text-white py-4 sm:py-5 px-6 sm:px-8 rounded font-bold text-lg text-center hover:bg-primary-dark transition-colors flex items-center justify-center gap-3 shadow-lg shadow-primary/20 active:scale-95"
                  >
                    <Navigation size={24} />
                    Pokreni Navigaciju
                  </a>
                </div>
              </div>

              {/* Social Links */}
              <div className="flex gap-4 sm:gap-6 justify-center lg:justify-start">
                <a 
                  href={businessInfo.social.facebook} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex-1 sm:flex-none flex items-center justify-center gap-3 px-6 py-3 bg-[#1877F2] text-white rounded font-bold hover:bg-[#1877F2]/90 transition-colors shadow-md active:scale-95"
                >
                  <Facebook size={24} />
                  Facebook
                </a>
                <a 
                  href={businessInfo.social.instagram} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex-1 sm:flex-none flex items-center justify-center gap-3 px-6 py-3 bg-[#E4405F] text-white rounded font-bold hover:bg-[#E4405F]/90 transition-colors shadow-md active:scale-95"
                >
                  <Instagram size={24} />
                  Instagram
                </a>
              </div>
            </div>

            {/* Map */}
            <div className="h-[400px] sm:h-[500px] lg:h-[600px] bg-gray-200 rounded shadow-2xl border-4 border-white overflow-hidden relative group">
              <iframe 
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2830.676872774636!2d20.3747223!3d44.8077778!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x475a6f7666666667%3A0x6666666666666666!2sGraditeljska%2026%2C%20Beograd!5e0!3m2!1sen!2srs!4v1620000000000!5m2!1sen!2srs" 
                width="100%" 
                height="100%" 
                style={{ border: 0 }} 
                allowFullScreen 
                loading="lazy"
                title="Lokacija Budimlija Elektro"
                className="grayscale group-hover:grayscale-0 transition-all duration-700"
              ></iframe>
              
              <div className="absolute bottom-4 left-4 sm:bottom-6 sm:left-6 bg-white p-3 sm:p-4 rounded shadow-lg border-l-4 border-primary max-w-[200px] sm:max-w-xs pointer-events-none">
                <p className="font-bold text-text text-sm sm:text-base flex items-center gap-2">
                  <MapPin size={16} className="text-primary shrink-0" />
                  Graditeljska 26, Beograd
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
